import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = () => axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ─── WORKER RESOURCES ───────────────────────────────────────────────
const CATEGORIES = [
  {
    id: 'des',
    icon: '🤝',
    color: 'from-violet-500 to-purple-600',
    badge: 'Free Service',
    title: 'Disability Employment Services (DES)',
    desc: 'Government-funded support to find and keep a job — at no cost to you',
    links: [
      { label: 'Find a DES provider near you', url: 'https://www.jobaccess.gov.au/employment-services', note: 'JobAccess · Free government service' },
      { label: 'What DES can do for you', url: 'https://www.dss.gov.au/disability-employment-services', note: 'DSS · Support with job finding, training & employer negotiation' },
      { label: 'NDIS & work — what you need to know', url: 'https://www.ndis.gov.au/participants/everyday-life/work-and-volunteering', note: 'NDIS · Work doesn\'t affect your plan' },
      { label: 'Ongoing support on the job', url: 'https://www.jobaccess.gov.au/employment-services/ongoing-support', note: 'JobAccess · Your DES provider supports you after you start' },
    ],
  },
  {
    id: 'rights',
    icon: '⚖️',
    color: 'from-blue-500 to-cyan-600',
    badge: 'Know Your Rights',
    title: 'Workplace Rights & Protections',
    desc: 'You have the same rights as every other worker — including the right to reasonable adjustments',
    links: [
      { label: 'Disability discrimination at work is illegal', url: 'https://www.humanrights.gov.au/our-work/disability-rights', note: 'Australian Human Rights Commission · Free complaint process' },
      { label: 'Fair Work — minimum wage & entitlements', url: 'https://www.fairwork.gov.au/pay-and-wages/minimum-wages', note: 'Fair Work · Know what you\'re owed' },
      { label: 'Supported Wage System (SWS) calculator', url: 'https://www.jobaccess.gov.au/supported-wage-system', note: 'JobAccess · Productivity-based wage top-up' },
      { label: 'Request reasonable adjustments', url: 'https://www.jobaccess.gov.au/workplace-adjustments', note: 'JobAccess · Employer must consider all reasonable requests' },
      { label: 'Workers compensation for disability', url: 'https://www.safeworkaustralia.gov.au/', note: 'Safe Work Australia · Injury & disability rights' },
    ],
  },
  {
    id: 'funding',
    icon: '💰',
    color: 'from-emerald-500 to-green-600',
    badge: 'Free Money',
    title: 'Funding & Financial Support',
    desc: 'Grants and subsidies you may be entitled to — don\'t leave money on the table',
    links: [
      { label: 'Employment Assistance Fund (EAF) — up to $6,000', url: 'https://www.jobaccess.gov.au/employment-assistance-fund', note: 'JobAccess · Fund for workplace modifications & equipment' },
      { label: 'NDIS employment support funding', url: 'https://www.ndis.gov.au/participants/everyday-life/work-and-volunteering', note: 'NDIS · May fund work-related supports in your plan' },
      { label: 'Disability Support Pension (DSP) + work', url: 'https://www.servicesaustralia.gov.au/disability-support-pension', note: 'Services Australia · Working doesn\'t always reduce DSP' },
      { label: 'Carer Allowance if supporting a carer', url: 'https://www.servicesaustralia.gov.au/carer-allowance', note: 'Services Australia · $153/fortnight' },
      { label: 'HEAS — help with expenses while job seeking', url: 'https://www.servicesaustralia.gov.au/home-equity-access-scheme', note: 'Services Australia · Job seeker payments available' },
    ],
  },
  {
    id: 'adjustments',
    icon: '🔧',
    color: 'from-orange-500 to-amber-600',
    badge: 'Your Rights',
    title: 'Workplace Adjustments',
    desc: 'Adjustments that help you thrive at work — employers are required to consider them',
    links: [
      { label: 'What adjustments can I request?', url: 'https://www.jobaccess.gov.au/workplace-adjustments/types', note: 'JobAccess · Equipment, hours, duties, environment' },
      { label: 'Assistive technology guide', url: 'https://www.and.org.au/resources', note: 'Australian Network on Disability · Tools & tech that help' },
      { label: 'Remote work & disability', url: 'https://www.jobaccess.gov.au/workplace/working-from-home', note: 'JobAccess · Remote work as a reasonable adjustment' },
      { label: 'Flexible hours & shift scheduling', url: 'https://www.fairwork.gov.au/leave/flexible-working-arrangements', note: 'Fair Work · Request flexible arrangements' },
    ],
  },
  {
    id: 'international',
    icon: '🌏',
    color: 'from-rose-500 to-pink-600',
    badge: 'International Workers',
    title: 'International Workers with Disability',
    desc: 'Living with a disability and working in Australia on a visa? Here\'s what you need to know.',
    links: [
      { label: 'DES is available to eligible visa holders', url: 'https://www.jobaccess.gov.au/employment-services', note: 'JobAccess · Some temporary visa holders qualify — check eligibility' },
      { label: 'Check your visa work conditions (VEVO)', url: 'https://immi.homeaffairs.gov.au/visas/already-have-a-visa/check-visa-details-and-conditions/check-conditions-online', note: 'Home Affairs · Free · Instant visa check' },
      { label: 'Disability visa — permanent residency pathway', url: 'https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/carer-116', note: 'Dept. of Home Affairs · Carer visa for those needing assistance' },
      { label: 'MARA-registered migration agent help', url: 'https://www.mara.gov.au/', note: 'OMARA · Get immigration advice for complex disability situations' },
      { label: 'International disability rights in Australia', url: 'https://www.humanrights.gov.au/our-work/disability-rights/international-disability-rights', note: 'AHRC · Australia\'s obligations under CRPD' },
    ],
  },
  {
    id: 'ndis',
    icon: '🧩',
    color: 'from-teal-500 to-cyan-600',
    badge: 'NDIS',
    title: 'NDIS — Getting the Most From Your Plan',
    desc: 'Your NDIS plan can fund work-related supports. Most people don\'t know this.',
    links: [
      { label: 'NDIS and working — full guide', url: 'https://www.ndis.gov.au/participants/everyday-life/work-and-volunteering', note: 'NDIS · Find out what supports can be funded' },
      { label: 'Apply for NDIS access', url: 'https://www.ndis.gov.au/applying-access-ndis', note: 'NDIS · Application process & eligibility' },
      { label: 'NDIS Local Area Coordinators', url: 'https://www.ndis.gov.au/participants/working-your-local-area-coordinator', note: 'NDIS · Free local help with your plan' },
      { label: 'Supported Independent Living (SIL)', url: 'https://www.ndis.gov.au/participants/everyday-life/supported-independent-living', note: 'NDIS · Housing support that enables you to work' },
      { label: 'Find a registered NDIS provider', url: 'https://www.ndis.gov.au/participants/working-providers/find-registered-provider', note: 'NDIS Provider Finder · Search by location & support type' },
    ],
  },
  {
    id: 'community',
    icon: '💬',
    color: 'from-indigo-500 to-blue-600',
    badge: 'Community',
    title: 'Community & Peer Support',
    desc: 'You\'re not alone. Connect with others who get it.',
    links: [
      { label: 'People with Disability Australia (PWDA)', url: 'https://pwd.org.au/', note: 'PWDA · Australia\'s national disability advocacy organisation' },
      { label: 'Disability advocacy — find your local service', url: 'https://www.disabilityadvocacyfinder.dss.gov.au/', note: 'DSS · Free advocacy in your area' },
      { label: 'Beyond Blue — mental health & disability', url: 'https://www.beyondblue.org.au/', note: 'Beyond Blue · 24/7 support line 1300 22 4636' },
      { label: 'Lifeline crisis support', url: 'https://www.lifeline.org.au/', note: 'Lifeline · 24/7 · 13 11 14' },
      { label: 'AND — Australian Network on Disability', url: 'https://www.and.org.au/', note: 'AND · Connects workers with inclusive employers' },
    ],
  },
  {
    id: 'transport',
    icon: '🚗',
    color: 'from-yellow-500 to-orange-500',
    badge: 'Getting to Work',
    title: 'Transport & Getting to Work',
    desc: 'Accessible transport options and funding to get you to your shifts',
    links: [
      { label: 'Companion Card — free travel companion', url: 'https://www.health.gov.au/our-work/companion-card', note: 'Dept. of Health · Your companion travels free on public transport' },
      { label: 'Companion Card NSW (apply here)', url: 'https://www.nsw.gov.au/community-services/companion-card', note: 'NSW Government · Apply if you\'re in NSW' },
      { label: 'Taxi subsidy scheme by state', url: 'https://www.and.org.au/resources/transport', note: 'AND · Check what your state offers' },
      { label: 'Transport for NSW accessibility', url: 'https://transportnsw.info/travel-info/ways-to-get-around/accessible-transport', note: 'Transport NSW · Accessible buses, trains, ferries' },
      { label: 'EAF can fund transport to interviews', url: 'https://www.jobaccess.gov.au/employment-assistance-fund', note: 'JobAccess · Apply for transport assistance funding' },
      { label: 'Uber Accessibility', url: 'https://www.uber.com/au/en/ride/uber-assist/', note: 'Uber Assist · Drivers trained to help passengers with disability' },
    ],
  },
];

// ─── SUBSIDY CALCULATOR ───────────────────────────────────────────────
const WAGE_SUBSIDIES = [
  { weeks: 13, amount: 4000, label: '13-week subsidy', desc: 'For job seekers with moderate support needs' },
  { weeks: 26, amount: 6500, label: '26-week subsidy', desc: 'For job seekers with higher support needs' },
  { weeks: 52, amount: 10950, label: '52-week subsidy', desc: 'Maximum — for job seekers with complex needs' },
];

// ─── ABILITY PROFILE MODAL ────────────────────────────────────────────
const DISABILITY_TYPES = [
  'Physical / Mobility', 'Vision / Blindness', 'Hearing / Deafness',
  'Intellectual / Learning', 'Mental Health', 'Autism Spectrum',
  'Chronic illness / Pain', 'Acquired Brain Injury', 'Prefer not to say',
];

const ADJUSTMENTS_OPTIONS = [
  'Flexible start/end times', 'Remote or hybrid work', 'Sit-down role',
  'Quiet environment', 'Extra breaks', 'Screen reader / assistive tech',
  'Modified duties', 'On-site support person', 'Accessible parking',
  'Ground floor / lift access', 'Large-print materials', 'No loud noise',
];

export default function AbilityHubPage() {
  const [activeTab, setActiveTab] = useState('resources');
  const [openCat, setOpenCat] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [calcWeeks, setCalcWeeks] = useState(26);
  const [profileForm, setProfileForm] = useState({
    disability_type: '',
    adjustments_needed: [],
    des_enrolled: false,
    ndis_participant: false,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const r = await API().get('/ability/profile');
      if (r.data) setProfile(r.data);
    } catch { /* no profile yet */ }
  };

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API().post('/ability/profile', profileForm);
      showToast('✅ Your AbilityHub profile saved');
      setShowProfileModal(false);
      loadProfile();
    } catch { showToast('❌ Could not save. Try again.', 'err'); }
    setSaving(false);
  };

  const toggleAdjustment = (adj) => {
    setProfileForm(f => ({
      ...f,
      adjustments_needed: f.adjustments_needed.includes(adj)
        ? f.adjustments_needed.filter(a => a !== adj)
        : [...f.adjustments_needed, adj],
    }));
  };

  const calcSubsidy = WAGE_SUBSIDIES.find(w => w.weeks === calcWeeks) || WAGE_SUBSIDIES[1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Skip to content — critical for screen reader / keyboard users */}
      <a href="#ability-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-violet-700 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold text-sm">
        Skip to main content
      </a>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all ${toast.type === 'err' ? 'bg-red-500' : 'bg-emerald-500'}`}>
          {toast.msg}
        </div>
      )}

      {/* ── HERO ── */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 pt-12 pb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-5xl">♿</span>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-white/20 backdrop-blur text-white text-xs font-semibold px-3 py-1 rounded-full">ShiftMatch AbilityHub</span>
                <span className="bg-emerald-400/30 text-emerald-100 text-xs font-semibold px-3 py-1 rounded-full">🆓 Free for all workers</span>
              </div>
              <h1 className="text-3xl font-black tracking-tight">Work Without Limits</h1>
            </div>
          </div>
          <p className="text-violet-100 text-lg max-w-2xl leading-relaxed">
            Australia's most inclusive short-shift platform. Whether you're Deaf, blind, use a wheelchair, live with chronic illness or mental health — you belong here, and we have your back.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={() => {
                // Pre-populate form with existing profile data when editing
                if (profile) {
                  setProfileForm({
                    disability_type: profile.disability_type || '',
                    adjustments_needed: profile.adjustments_needed || [],
                    des_enrolled: profile.des_enrolled || false,
                    ndis_participant: profile.ndis_participant || false,
                    notes: profile.notes || '',
                  });
                }
                setShowProfileModal(true);
              }}
              className="bg-white text-violet-700 font-bold px-5 py-2.5 rounded-xl hover:bg-violet-50 transition text-sm shadow"
            >
              {profile ? '\u270F\uFE0F Edit My Profile' : '+ Set Up My Profile'}
            </button>
            <a
              href="#government-incentives"
              className="bg-white/20 backdrop-blur text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-white/30 transition text-sm border border-white/30"
            >
              📊 For Employers & Government
            </a>
          </div>

          {profile && (
            <div className="mt-5 bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20 inline-block">
              <p className="text-sm text-violet-100 font-medium">Your Profile</p>
              <p className="text-white font-bold">{profile.disability_type || 'Prefer not to say'}</p>
              {profile.adjustments_needed?.length > 0 && (
                <p className="text-violet-200 text-xs mt-1">{profile.adjustments_needed.slice(0, 3).join(' · ')}{profile.adjustments_needed.length > 3 ? ` +${profile.adjustments_needed.length - 3} more` : ''}</p>
              )}
            </div>
          )}
        </div>

        {/* Stats bar */}
        <div className="bg-black/20 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-4 py-4 grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-black text-white">4.3M</p>
              <p className="text-violet-200 text-xs">Australians with disability of working age</p>
            </div>
            <div>
              <p className="text-2xl font-black text-white">$10,950</p>
              <p className="text-violet-200 text-xs">Max gov't wage subsidy for employers who hire</p>
            </div>
            <div>
              <p className="text-2xl font-black text-white">53%</p>
              <p className="text-violet-200 text-xs">Employment gap vs. non-disabled Australians</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="AbilityHub sections">
            {[
              { id: 'resources', label: '📚 Resources' },
              { id: 'subsidies', label: '💰 Subsidies Calculator' },
              { id: 'shifts', label: '🔍 Accessible Shifts' },
              { id: 'employers', label: '🏢 For Employers' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-label={`View ${tab.label} section`}
                aria-selected={activeTab === tab.id}
                role="tab"
                className={`px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main id="ability-main" className="max-w-4xl mx-auto px-4 py-8">

        {/* ── RESOURCES TAB ── */}
        {activeTab === 'resources' && (
          <div className="space-y-4">
            <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 text-sm text-violet-800 flex gap-3">
              <span className="text-xl">💡</span>
              <div>
                <strong>Tip:</strong> Start with <strong>DES (Disability Employment Services)</strong> — it's free, government-funded, and a DES provider can negotiate workplace adjustments on your behalf, connect you to funding, and give you ongoing support on the job.
              </div>
            </div>

            {CATEGORIES.map(cat => (
              <div key={cat.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <button
                  className="w-full text-left p-5 flex items-center gap-4"
                  onClick={() => setOpenCat(openCat === cat.id ? null : cat.id)}
                  aria-expanded={openCat === cat.id}
                  aria-label={`${openCat === cat.id ? 'Collapse' : 'Expand'} ${cat.title}`}
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${cat.color} rounded-xl flex items-center justify-center text-2xl flex-shrink-0`}>
                    {cat.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-bold text-gray-900 text-base">{cat.title}</h3>
                      <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full">{cat.badge}</span>
                    </div>
                    <p className="text-gray-500 text-sm">{cat.desc}</p>
                  </div>
                  <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${openCat === cat.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {openCat === cat.id && (
                  <div className="border-t border-gray-100">
                    {cat.links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 group"
                      >
                        <div className="w-2 h-2 rounded-full bg-violet-400 mt-2 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 group-hover:text-violet-700 transition-colors">{link.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{link.note}</p>
                        </div>
                        <svg className="w-4 h-4 text-gray-400 group-hover:text-violet-500 transition-colors mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── SUBSIDY CALCULATOR TAB ── */}
        {activeTab === 'subsidies' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Government Wage Subsidy Calculator</h2>
              <p className="text-gray-500 text-sm mb-6">If you're on a DES program, your employer can receive up to <strong>$10,950</strong> in wage subsidies for hiring you. Use this to show employers the financial case.</p>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">Expected employment duration</label>
                <div className="grid grid-cols-3 gap-3">
                  {WAGE_SUBSIDIES.map(w => (
                    <button
                      key={w.weeks}
                      onClick={() => setCalcWeeks(w.weeks)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${calcWeeks === w.weeks ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <p className="font-bold text-lg text-gray-900">{w.weeks}w</p>
                      <p className="text-xs text-gray-500">{w.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-6 text-white text-center mb-6">
                <p className="text-violet-200 text-sm mb-1">Government wage subsidy for your employer</p>
                <p className="text-5xl font-black">${calcSubsidy.amount.toLocaleString()}</p>
                <p className="text-violet-200 text-sm mt-1">over {calcSubsidy.weeks} weeks</p>
                <p className="text-violet-100 text-xs mt-3 opacity-80">{calcSubsidy.desc}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                  <p className="text-emerald-700 text-xs font-semibold mb-1">Employment Assistance Fund</p>
                  <p className="text-2xl font-black text-emerald-700">+$6,000</p>
                  <p className="text-emerald-600 text-xs">for workplace modifications</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-blue-700 text-xs font-semibold mb-1">Total employer support</p>
                  <p className="text-2xl font-black text-blue-700">${(calcSubsidy.amount + 6000).toLocaleString()}</p>
                  <p className="text-blue-600 text-xs">combined potential value</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-sm font-semibold mb-1">📄 Share this with your employer</p>
                <p className="text-amber-700 text-xs leading-relaxed">
                  "Hiring me through a DES provider may entitle you to a government wage subsidy of up to ${calcSubsidy.amount.toLocaleString()} over {calcSubsidy.weeks} weeks, plus up to $6,000 through the Employment Assistance Fund for workplace modifications. Contact JobAccess on 1800 464 800 to find out more."
                </p>
                <button
                  onClick={() => navigator.clipboard?.writeText(`Hiring me through a DES provider may entitle you to a government wage subsidy of up to $${calcSubsidy.amount.toLocaleString()} over ${calcSubsidy.weeks} weeks, plus up to $6,000 through the Employment Assistance Fund for workplace modifications. Contact JobAccess on 1800 464 800 to find out more.`).then(() => showToast('📋 Copied to clipboard!'))}
                  className="mt-2 text-xs bg-amber-200 hover:bg-amber-300 text-amber-900 font-semibold px-3 py-1.5 rounded-lg transition"
                >
                  Copy to clipboard
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">Supported Wage System (SWS)</h3>
              <p className="text-gray-500 text-sm mb-4">
                If your disability affects your productivity, SWS lets you be paid based on your productive output — with a government top-up to the minimum wage. This protects both you and the employer.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { label: 'Productivity assessed', value: 'By a licensed assessor', icon: '🔍' },
                  { label: 'Minimum wage guaranteed', value: 'Government tops up the gap', icon: '💰' },
                  { label: 'Annual review', value: 'Assessment updated yearly', icon: '📅' },
                  { label: 'Employer savings', value: 'Pay only assessed productivity', icon: '📉' },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xl mb-1">{item.icon}</p>
                    <p className="font-semibold text-gray-800 text-xs">{item.label}</p>
                    <p className="text-gray-500 text-xs">{item.value}</p>
                  </div>
                ))}
              </div>
              <a href="https://www.jobaccess.gov.au/supported-wage-system" target="_blank" rel="noopener noreferrer"
                className="mt-4 inline-block text-sm text-violet-600 font-semibold hover:underline">
                Learn more about SWS →
              </a>
            </div>
          </div>
        )}

        {/* ── ACCESSIBLE SHIFTS TAB ── */}
        {activeTab === 'shifts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Find Accessible Shifts</h2>
              <p className="text-gray-500 text-sm mb-5">Shifts tagged as disability-friendly by inclusive employers. Every employer with an Inclusive Employer badge has committed to reasonable adjustments.</p>

              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { label: 'Sit-down roles', icon: '🪑', tag: 'sit_down' },
                  { label: 'Work from home', icon: '🏠', tag: 'remote' },
                  { label: 'Quiet environment', icon: '🔇', tag: 'quiet' },
                  { label: 'Flexible hours', icon: '⏰', tag: 'flexible' },
                  { label: 'Ground floor', icon: '🏢', tag: 'accessible_venue' },
                  { label: 'Short shifts', icon: '⚡', tag: 'short_shift' },
                ].map(f => (
                  <button key={f.tag} className="flex items-center gap-2 p-3 rounded-xl border-2 border-violet-100 bg-violet-50 hover:border-violet-400 transition text-left text-sm font-semibold text-violet-800">
                    <span className="text-xl">{f.icon}</span> {f.label}
                  </button>
                ))}
              </div>

              <Link
                to="/worker"
                className="block w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white text-center font-bold py-3 rounded-xl hover:opacity-90 transition text-sm"
              >
                Browse All Shifts →
              </Link>
            </div>

            <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl border border-violet-100 p-6">
              <h3 className="font-bold text-gray-900 mb-2">💡 How to Signal Your Needs</h3>
              <p className="text-gray-600 text-sm mb-4">You never have to disclose your disability. But if you want to, your profile lets you:</p>
              <ul className="space-y-2">
                {[
                  'Note your required adjustments (visible only to employers you apply to)',
                  'Filter shifts tagged as disability-friendly',
                  'Connect your DES provider to advocate on your behalf',
                  'Add a short note about your work style to your public profile',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-violet-500 mt-0.5 font-bold">✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* ── FOR EMPLOYERS TAB ── */}
        {activeTab === 'employers' && (
          <div id="government-incentives" className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 text-white">
              <h2 className="text-2xl font-black mb-2">The Business Case for Inclusive Hiring</h2>
              <p className="text-indigo-100 text-sm leading-relaxed">
                Companies that hire workers with disability report higher loyalty, lower turnover, and up to <strong className="text-white">$10,950 in government subsidies per hire</strong>. It's not just the right thing — it's smart business.
              </p>
            </div>

            {/* Incentives grid */}
            <div className="grid grid-cols-1 gap-4">
              {[
                {
                  icon: '💵',
                  title: 'Wage Subsidy — up to $10,950',
                  desc: 'Employers who hire eligible job seekers through DES receive wage subsidies paid fortnightly. The longer the employment, the higher the subsidy.',
                  cta: 'Learn more', url: 'https://www.jobaccess.gov.au/employers/wage-subsidies',
                  color: 'border-emerald-200 bg-emerald-50',
                  badge: 'Most Popular',
                },
                {
                  icon: '🔧',
                  title: 'Employment Assistance Fund — up to $6,000',
                  desc: 'Need a ramp, screen reader, ergonomic chair, or modified uniform? EAF covers it. Applies to equipment, building modifications, and Auslan interpreter services.',
                  cta: 'Apply for EAF', url: 'https://www.jobaccess.gov.au/employment-assistance-fund',
                  color: 'border-blue-200 bg-blue-50',
                },
                {
                  icon: '🧩',
                  title: 'DES Providers Manage the Hard Parts',
                  desc: 'A free DES provider does the job matching, arranges trial periods, handles adjustments, provides ongoing support, and follows up if issues arise. You don\'t do it alone.',
                  cta: 'Find a DES provider', url: 'https://www.jobaccess.gov.au/employment-services/find-employment-service',
                  color: 'border-purple-200 bg-purple-50',
                },
                {
                  icon: '📊',
                  title: 'ESG & Diversity Reporting',
                  desc: 'ShiftMatch tracks and reports your inclusive hiring metrics. Get your diversity dashboard, percentage of shifts filled by workers with disability, and downloadable ESG report.',
                  cta: 'Coming soon', url: '#',
                  color: 'border-amber-200 bg-amber-50',
                  badge: 'Coming Soon',
                },
                {
                  icon: '🏅',
                  title: 'Inclusive Employer Badge',
                  desc: 'Post 3+ shifts tagged as disability-friendly and hire at least one worker with disability to earn the verified Inclusive Employer badge. Featured placement in search results.',
                  cta: 'Get verified', url: '#',
                  color: 'border-rose-200 bg-rose-50',
                },
                {
                  icon: '💸',
                  title: 'Reduced ShiftMatch Commission',
                  desc: 'Verified Inclusive Employers pay 10% commission (vs standard 15%) for any shift filled by a worker registered with AbilityHub. A direct financial incentive to hire inclusively.',
                  cta: 'Learn more', url: '#',
                  color: 'border-teal-200 bg-teal-50',
                  badge: 'ShiftMatch Exclusive',
                },
              ].map((card, i) => (
                <div key={i} className={`rounded-2xl border p-5 ${card.color}`}>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{card.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900">{card.title}</h3>
                        {card.badge && <span className="bg-gray-900 text-white text-xs font-semibold px-2 py-0.5 rounded-full">{card.badge}</span>}
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-3">{card.desc}</p>
                      {card.url !== '#' && (
                        <a href={card.url} target="_blank" rel="noopener noreferrer"
                          className="text-sm font-semibold text-violet-700 hover:underline">
                          {card.cta} →
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Government partners section */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-1">Government & Institutional Partners</h3>
              <p className="text-gray-500 text-sm mb-4">ShiftMatch works alongside these bodies to connect employers with the programs that make inclusive hiring easy.</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: 'JobAccess', desc: 'National gateway for disability employment', url: 'https://www.jobaccess.gov.au' },
                  { name: 'DSS', desc: 'Department of Social Services — DES funding', url: 'https://www.dss.gov.au' },
                  { name: 'NDIS', desc: 'National Disability Insurance Scheme', url: 'https://www.ndis.gov.au' },
                  { name: 'Fair Work', desc: 'Workplace rights & wage compliance', url: 'https://www.fairwork.gov.au' },
                  { name: 'AHRC', desc: 'Human rights — disability discrimination', url: 'https://www.humanrights.gov.au' },
                  { name: 'AND', desc: 'Australian Network on Disability', url: 'https://www.and.org.au' },
                ].map(p => (
                  <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                    className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition group">
                    <p className="font-bold text-gray-800 text-sm group-hover:text-violet-700">{p.name}</p>
                    <p className="text-gray-500 text-xs">{p.desc}</p>
                  </a>
                ))}
              </div>
            </div>

            {/* Contact section */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 text-white text-center">
              <h3 className="text-xl font-black mb-2">Want a Partnership Discussion?</h3>
              <p className="text-violet-100 text-sm mb-4">We're actively seeking government, industry, and DES provider partnerships to expand AbilityHub across Australia.</p>
              <a
                href="mailto:hello@shiftmatch.work?subject=AbilityHub Partnership"
                className="bg-white text-violet-700 font-bold px-6 py-2.5 rounded-xl hover:bg-violet-50 transition inline-block text-sm shadow"
              >
                Get in Touch →
              </a>
            </div>
          </div>
        )}
      </main>

      {/* ── PROFILE MODAL ── */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-black text-gray-900">My AbilityHub Profile</h3>
                <p className="text-gray-500 text-xs">Private — only shared when you apply for a shift</p>
              </div>
              <button onClick={() => setShowProfileModal(false)} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close profile editor">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={saveProfile} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Disability type (optional)</label>
                <select
                  value={profileForm.disability_type}
                  onChange={e => setProfileForm(f => ({ ...f, disability_type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">Prefer not to say</option>
                  {DISABILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Adjustments I may need</label>
                <div className="grid grid-cols-2 gap-2">
                  {ADJUSTMENTS_OPTIONS.map(adj => (
                    <button
                      key={adj}
                      type="button"
                      onClick={() => toggleAdjustment(adj)}
                      aria-pressed={profileForm.adjustments_needed.includes(adj)}
                      aria-label={`${profileForm.adjustments_needed.includes(adj) ? 'Remove' : 'Add'} adjustment: ${adj}`}
                      className={`text-left px-3 py-2 rounded-lg border text-xs font-medium transition-all ${profileForm.adjustments_needed.includes(adj) ? 'border-violet-500 bg-violet-50 text-violet-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    >
                      {adj}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={profileForm.des_enrolled}
                    onChange={e => setProfileForm(f => ({ ...f, des_enrolled: e.target.checked }))}
                    className="accent-violet-600"
                  />
                  <div>
                    <p className="text-xs font-semibold text-gray-800">DES enrolled</p>
                    <p className="text-xs text-gray-500">I have a DES provider</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={profileForm.ndis_participant}
                    onChange={e => setProfileForm(f => ({ ...f, ndis_participant: e.target.checked }))}
                    className="accent-violet-600"
                  />
                  <div>
                    <p className="text-xs font-semibold text-gray-800">NDIS participant</p>
                    <p className="text-xs text-gray-500">I have an active NDIS plan</p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Work style note (optional)</label>
                <textarea
                  value={profileForm.notes}
                  onChange={e => setProfileForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. 'I work best in structured roles. I'm Deaf and communicate via text/email.'"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                />
              </div>

              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500">
                <strong className="text-gray-700">Privacy:</strong> This profile is never shown publicly. It's used to filter relevant shifts and, only if you choose, to share with an employer when you apply.
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white font-bold py-3 rounded-xl hover:opacity-90 transition disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save My Profile'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-4 pb-10 text-center">
        <p className="text-gray-400 text-xs">
          ShiftMatch AbilityHub does not provide legal, medical, or financial advice. Government program details are provided for information only. Contact JobAccess on <strong>1800 464 800</strong> for official guidance.
        </p>
      </div>
    </div>
  );
}
